import {
  APIScope,
  APIScopePermission,
  DefaultRoleTypes,
  DefaultRoles,
  GroupInterface,
  LimitToLocationScopes,
  Log,
  MemberPermission,
  RoleInterface,
  RoleInterfaceMap
} from "@swivl-mono/types";

import { CreateLogger } from "../Loggers/SwivlLoggers";

const log = CreateLogger(Log.AUTH, "check-permissions");

function getDefaultRoleBySlug(slug: string): RoleInterface | undefined {
  if (Object.values(DefaultRoleTypes).includes(slug as DefaultRoleTypes)) {
    return DefaultRoles[slug as DefaultRoleTypes];
  }
  return undefined;
}

function checkPermissionInRole(parentRoles: RoleInterfaceMap | undefined, role: RoleInterface, scope: APIScope): APIScopePermission {
  // Check if the role has an explicit permission for this scope
  if (role && role.actions && role.actions[scope]) {
    if (role.actions[scope] === APIScopePermission.ALLOWED) {
      return APIScopePermission.ALLOWED;
    }
    if (role.actions[scope] === APIScopePermission.FORBIDDEN) {
      return APIScopePermission.FORBIDDEN;
    }
  }

  // Check parent role if defined
  if (role.parentSlug) {
    const parentRole = parentRoles?.[role.parentSlug] ?? getDefaultRoleBySlug(role.parentSlug);
    if (parentRole) {
      return checkPermissionInRole(parentRoles, parentRole, scope);
    }
  }

  // Fall back to default role permissions if slug matches a default role type
  const defaultRole = getDefaultRoleBySlug(role.slug);
  if (defaultRole && defaultRole.actions[scope]) {
    if (defaultRole.actions[scope] === APIScopePermission.ALLOWED) {
      return APIScopePermission.ALLOWED;
    }
    if (defaultRole.actions[scope] === APIScopePermission.FORBIDDEN) {
      return APIScopePermission.FORBIDDEN;
    }
  }

  return APIScopePermission.FORBIDDEN; // Default to forbidden if not explicitly allowed or inherited
}

export function Internal_CheckPermissions(
  parentRoles: RoleInterfaceMap | undefined,
  roles: RoleInterfaceMap,
  memberPermissions: MemberPermission[],
  scope: APIScope,
  orgId: number,
  groupIdParam?: number,
  locationId?: number
): boolean {
  log("Internal_CheckPermissions :", memberPermissions, scope, orgId, groupIdParam, locationId);

  // Iterate over each member permission
  for (const memberPermission of memberPermissions) {
    log("memberPermission :locations", memberPermission.locations);
    // if (memberPermission.orgId !== orgId) continue; // Skip if orgId does not match

    const role = roles?.[memberPermission.roleSlug] || parentRoles?.[memberPermission.roleSlug] || getDefaultRoleBySlug(memberPermission.roleSlug);

    log("checking", scope, locationId, groupIdParam, role);
    if (!role) continue; // Skip if role is not found

    if (!LimitToLocationScopes.includes(scope)) {
      if (checkPermissionInRole(parentRoles, role, scope) === APIScopePermission.ALLOWED) {
        log("mk0");

        return true;
      }
    }
    // Check permissions for the organization level
    log("checking org id", memberPermission.locations.orgId, orgId);
    if (memberPermission.locations.orgId === orgId) {
      if (checkPermissionInRole(parentRoles, role, scope) === APIScopePermission.ALLOWED) {
        log("mk1");
        return true;
      }
    }

    // Check permissions for specific group
    if (groupIdParam && memberPermission.locations.groupIds?.includes(groupIdParam)) {
      if (checkPermissionInRole(parentRoles, role, scope) === APIScopePermission.ALLOWED) {
        log("mk2");

        return true;
      }
    }
    // Check permissions for specific location
    if (locationId && memberPermission.locations.locationIds?.includes(locationId)) {
      if (checkPermissionInRole(parentRoles, role, scope) === APIScopePermission.ALLOWED) {
        log("mk3");

        return true;
      }
    }
  }
  log("mk4");

  return false;
}

/**
 * Resolve the set of location ids a member may act on for a given scope,
 * honoring each role assignment's `LocationsMatrix` individually.
 *
 * This exists because `Internal_CheckPermissions` grants scopes NOT in
 * `LimitToLocationScopes` org-wide as soon as any role assignment's role
 * allows them — its short-circuit makes it unusable for computing a
 * per-location allow-list for scopes like `tickets-view` / `tickets-edit`
 * (ENG-2882). Here the matrix is always consulted: an assignment only
 * contributes the locations its own matrix names.
 *
 * Returns `"all"` when any qualifying assignment is org-wide, otherwise the
 * deduped union of location ids across qualifying assignments (group ids are
 * expanded via `groups`). An empty array means no location access at all.
 */
export function Internal_ResolveScopedLocationIds(
  parentRoles: RoleInterfaceMap | undefined,
  roles: RoleInterfaceMap,
  memberPermissions: MemberPermission[],
  scope: APIScope,
  orgId: number,
  groups: GroupInterface[]
): number[] | "all" {
  const allowed = new Set<number>();

  for (const memberPermission of memberPermissions ?? []) {
    const role = roles?.[memberPermission.roleSlug] || parentRoles?.[memberPermission.roleSlug] || getDefaultRoleBySlug(memberPermission.roleSlug);
    if (!role) continue;
    if (checkPermissionInRole(parentRoles, role, scope) !== APIScopePermission.ALLOWED) continue;

    const locations = memberPermission.locations;
    if (!locations) continue;

    if (locations.orgId === orgId) {
      return "all";
    }
    if (locations.groupIds?.length) {
      for (const group of groups) {
        if (!locations.groupIds.includes(group.id)) continue;
        for (const location of group.locations ?? []) {
          allowed.add(location.id);
        }
      }
    }
    for (const locationId of locations.locationIds ?? []) {
      allowed.add(locationId);
    }
  }

  return Array.from(allowed);
}

// interface MemberPermissionWithRole extends MemberPermission {
//     role: RoleInterface
// }

// //NOTE: this function should be wrapped in a helper function.

// export function Internal_CheckPermissions2(
//     parentRoles: RoleInterfaceMap | undefined,
//     roles:RoleInterfaceMap,
//     memberPermissions:MemberPermission[],
//     scope:APIScope,
//     orgId: number,
//     groupIdParam?: number,
//     locationId?: number,
// ):boolean {

//     const grants = (AnyScopeAPIScope.includes(scope)) ? memberPermissions : AuthGetScopedPermissions(memberPermissions, orgId, groupIdParam, locationId);
//     if (!grants?.length) {
//         log('No grants found for this action', memberPermissions, scope)
//         return false;
//     }
//     log('grants :', grants);

//     let grantsWithRoles: MemberPermissionWithRole[] = []
//     log('grantsWithRoles 1:', grantsWithRoles);

//     for (let i = 0; i < grants.length; i++) {
//         const grant = grants[i];
//         if (roles && roles[grant.roleSlug]) {
//             grantsWithRoles.push({
//                 ...grant,
//                 role: roles[grant.roleSlug]
//             })

//         } else if (parentRoles && parentRoles[grant.roleSlug]) {
//             grantsWithRoles.push({
//                 ...grant,
//                 role: parentRoles[grant.roleSlug]
//             })
//         } else {
//
//             return false;
//         }
//     }

//     log('grantsWithRoles 2:', grantsWithRoles);
//     log("Roles : ", roles);

//     const filteredRoles = Object.values(roles).filter((role) => {
//         return grants.some((grant) => {
//             return role.slug === grant.roleSlug;
//         });
//     })

//     log("Roles : ", roles);
//     log('filteredRoles :', filteredRoles);

//     if (!filteredRoles.length) {
//
//         return false;
//     }

//    // Pass in parent roles here too.
//     const allowed = AllowedScopesForRoles(parentRoles, filteredRoles).includes(scope);
//     log('allowed :', allowed);

//     return true;
// }
